import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import {
    App,
    Button,
    DatePicker,
    Flex,
    Form,
    Image,
    Input,
    Modal,
    Select,
    Space,
    Tag,
    Typography,
} from 'antd';
import { useSetState } from 'ahooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Player from 'xgplayer';
import HlsPlugin from 'xgplayer-hls';

import Permission from '@/components/permission';
import {
    commentDelete,
    commentList,
    type CommentItem,
    type CommentListParams,
} from '@/services/comments';
import CommentAuditLogDrawer from '@/pages/comments/components/audit-log-drawer';
import { formatDramaTimestamp, resolveAssetUrl } from '@/pages/drama-review/utils';
import { isAbortError, RequestGuard } from '@/utils/request-guard';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';
import type { Dayjs } from 'dayjs';

import 'xgplayer/dist/index.min.css';

const PERMISSION = 'Dramas_Comments';
const DELETE_PERMISSION = 'Dramas_Comments_Delete';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

type AuditStatusFilter = 'APPROVED' | 'DELETED' | '';
type LevelFilter = 'root' | 'reply' | '';
type SortDirection = 'asc' | 'desc';

type SearchFilters = {
    id?: string;
    content?: string;
    user_id?: string;
    item_type?: 'EPISODE' | 'SHORT_VIDEO';
    work_id?: string;
    level?: LevelFilter;
    audit_status_ui?: AuditStatusFilter;
    created_at_range?: [Dayjs, Dayjs];
};

const CommentVideoPreviewModal: React.FC<{
    open: boolean;
    src: string | null;
    onClose: () => void;
}> = ({ open, src, onClose }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<Player | null>(null);

    useEffect(() => {
        if (!open || !src) return;

        const timer = window.setTimeout(() => {
            if (!containerRef.current) return;
            const isHls = src.includes('.m3u8');
            playerRef.current = new Player({
                el: containerRef.current,
                url: src,
                autoplay: true,
                playsinline: true,
                plugins: isHls ? [HlsPlugin] : [],
                fitVideoSize: 'auto' as any,
                videoFillMode: 'contain',
                height: 420,
                width: '100%',
            });
        }, 50);

        return () => {
            window.clearTimeout(timer);
            playerRef.current?.destroy();
            playerRef.current = null;
        };
    }, [open, src]);

    return (
        <Modal
            open={open}
            title={<FormattedMessage id="comments.previewTitle" />}
            footer={null}
            width={800}
            destroyOnClose
            onCancel={onClose}
        >
            <div ref={containerRef} style={{ width: '100%', minHeight: 420, background: '#000' }} />
        </Modal>
    );
};

const isDeleted = (record: CommentItem) =>
    record.deleted_at != null && record.deleted_at !== '';

const isRootComment = (record: CommentItem) =>
    record.is_root === 1 || record.is_root === true || record.is_root === '1';

const toCount = (value?: number | string | null) => {
    if (value === undefined || value === null || value === '') return '0';
    return String(value);
};

const buildListParams = (
    page: number,
    pageSize: number,
    filters: SearchFilters,
): CommentListParams => {
    const params: CommentListParams = {
        page,
        size: pageSize,
    };

    if (filters.id) params.id = filters.id;
    if (filters.content) params.content = filters.content;
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.item_type) params.item_type = filters.item_type;
    if (filters.work_id) params.work_id = filters.work_id;

    if (filters.level === 'root') params.is_root = 1;
    if (filters.level === 'reply') params.is_root = 0;

    if (filters.audit_status_ui === 'APPROVED') {
        params.audit_status = 'APPROVED';
    } else if (filters.audit_status_ui === 'DELETED') {
        // OpenAPI 无独立「已删除」枚举；用 deleted_at 区间筛软删记录
        params['deleted_at[]'] = [
            dayjs(0).toISOString(),
            dayjs().endOf('day').toISOString(),
        ];
    }

    if (filters.created_at_range?.length === 2) {
        params['created_at[]'] = [
            filters.created_at_range[0].startOf('day').toISOString(),
            filters.created_at_range[1].endOf('day').toISOString(),
        ];
    }

    return params;
};

const pickSearchFilters = (values: Record<string, unknown>): SearchFilters => {
    const next: SearchFilters = {};
    const id = String(values.id ?? '').trim();
    if (id) next.id = id;
    const content = String(values.content ?? '').trim();
    if (content) next.content = content;
    const userId = String(values.user_id ?? '').trim();
    if (userId) next.user_id = userId;
    const workId = String(values.work_id ?? '').trim();
    if (workId) next.work_id = workId;

    const itemType = String(values.item_type ?? '').trim();
    if (itemType === 'EPISODE') next.item_type = itemType;

    const level = String(values.level ?? '').trim();
    if (level === 'root' || level === 'reply') next.level = level;

    const audit = String(values.audit_status_ui ?? '').trim();
    if (audit === 'APPROVED' || audit === 'DELETED') next.audit_status_ui = audit;

    const range = values.created_at_range;
    if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
        next.created_at_range = range as [Dayjs, Dayjs];
    }

    return next;
};

const getDramaId = (record: CommentItem) =>
    String(record.drama?.id || record.episode?.drama?.id || record.episode?.drama_id || '').trim();

const getWorkTitle = (record: CommentItem) => {
    const dramaTitle = record.drama?.title?.trim() || record.episode?.drama?.title?.trim();
    const episodeTitle = record.episode?.title?.trim();
    return dramaTitle || episodeTitle || '';
};

const getWorkCover = (record: CommentItem) =>
    record.drama?.cover_url ||
    record.episode?.drama?.cover_url ||
    record.episode?.cover_url ||
    '';

const getVideoUrl = (record: CommentItem) =>
    record.episode?.hls_url || record.episode?.video_url || '';

const sortByCreatedAt = (rows: CommentItem[], direction: SortDirection) => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
        const av = Number(a.created_at) || 0;
        const bv = Number(b.created_at) || 0;
        return direction === 'asc' ? av - bv : bv - av;
    });
    return sorted;
};

const PageComments: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const [dataSource, setDataSource] = useState<CommentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [deleteTarget, setDeleteTarget] = useState<CommentItem | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [logBizId, setLogBizId] = useState<string | null>(null);
    const [logOpen, setLogOpen] = useState(false);
    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [searchForm] = Form.useForm();
    const [searchParams] = useSearchParams();
    const deeplinkAppliedRef = useRef(false);
    const [deleteForm] = Form.useForm<{ reason: string }>();
    const requestGuard = useMemo(() => new RequestGuard(), []);

    const fetchList = useCallback(
        async (page: number, pageSize: number, filters = searchFilters, sort = sortDirection) => {
            const { id, signal } = requestGuard.begin();
            setLoading(true);
            try {
                const { data } = await commentList(buildListParams(page, pageSize, filters), {
                    signal,
                });
                if (!requestGuard.isCurrent(id)) return;

                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                const rows = Array.isArray(data.data) ? data.data : [];
                setDataSource(sortByCreatedAt(rows, sort));
                setPagination({
                    current: Number(data.meta?.page ?? page) || 1,
                    pageSize: Number(data.meta?.size ?? pageSize) || DEFAULT_PAGINATION.pageSize,
                    total: data.meta?.total ?? rows.length,
                });
            } catch (error: any) {
                if (isAbortError(error) || signal.aborted) return;
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'comments.fetchFailed' }),
                );
            } finally {
                if (requestGuard.isCurrent(id)) setLoading(false);
            }
        },
        [formatMessage, message, requestGuard, searchFilters, setPagination, sortDirection],
    );

    const handleSearch = useCallback(() => {
        const nextFilters = pickSearchFilters(searchForm.getFieldsValue());
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters(nextFilters);
    }, [searchForm, setPagination]);

    const handleResetSearch = useCallback(() => {
        searchForm.resetFields();
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters({});
        setSortDirection('desc');
    }, [searchForm, setPagination]);

    const handleDeleteSubmit = useCallback(
        async (values: { reason: string }) => {
            if (!deleteTarget) return;

            const reason = values.reason?.trim() || '';
            if (!reason) {
                message.error(formatMessage({ id: 'comments.deleteReasonRequired' }));
                return;
            }

            setDeleting(true);
            try {
                // 后台账号 id 为 UUID，不符合 operatorId 数字校验；操作人由 Bearer Token 识别
                const { data } = await commentDelete(deleteTarget.id, { reason });
                if (data.code !== 200) {
                    message.error(data.msg || formatMessage({ id: 'comments.deleteFailed' }));
                    return;
                }
                message.success(formatMessage({ id: 'comments.deleteSuccess' }));
                setDeleteTarget(null);
                deleteForm.resetFields();
                void fetchList(
                    Number(pagination.current) || 1,
                    Number(pagination.pageSize) || 20,
                );
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'comments.deleteFailed' }),
                );
            } finally {
                setDeleting(false);
            }
        },
        [
            deleteForm,
            deleteTarget,
            fetchList,
            formatMessage,
            message,
            pagination.current,
            pagination.pageSize,
        ],
    );

    useEffect(() => {
        if (deeplinkAppliedRef.current) return;
        const id = searchParams.get('id')?.trim();
        if (!id) return;
        deeplinkAppliedRef.current = true;
        searchForm.setFieldsValue({ id });
        setSearchFilters({ id });
    }, [searchForm, searchParams]);

    useEffect(() => {
        void fetchList(1, Number(pagination.pageSize) || 20, searchFilters, sortDirection);
        return () => requestGuard.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 筛选/排序变化时从第 1 页重拉
    }, [searchFilters, sortDirection, fetchList, requestGuard]);

    const columns = useMemo<ProColumns<CommentItem>[]>(
        () => [
            {
                title: <FormattedMessage id="comments.columns.no" />,
                width: 64,
                align: 'center',
                render: (_, __, index) => {
                    const current = Number(pagination.current) || 1;
                    const pageSize = Number(pagination.pageSize) || 20;
                    return (current - 1) * pageSize + index + 1;
                },
            },
            {
                title: <FormattedMessage id="comments.columns.id" />,
                width: 200,
                render: (_, record) => (
                    <Typography.Text copyable={{ text: record.id }}>{record.id}</Typography.Text>
                ),
            },
            {
                title: <FormattedMessage id="comments.columns.content" />,
                width: 240,
                render: (_, record) => {
                    const text = record.content?.trim();
                    if (!text) return '-';
                    return (
                        <Typography.Paragraph
                            ellipsis={{ rows: 3, expandable: 'collapsible' }}
                            style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                            {text}
                        </Typography.Paragraph>
                    );
                },
            },
            {
                title: <FormattedMessage id="comments.columns.level" />,
                width: 260,
                render: (_, record) => {
                    if (isRootComment(record)) {
                        return (
                            <Tag color="blue">
                                <FormattedMessage id="comments.level.root" />
                            </Tag>
                        );
                    }
                    const nickname =
                        record.reply_to_user?.nickname?.trim() ||
                        formatMessage({ id: 'comments.level.reply' });
                    const uid =
                        record.reply_to_user?.id ||
                        record.reply_to_user_id ||
                        '-';
                    return (
                        <Space direction="vertical" size={4}>
                            <Tag color="purple">
                                <FormattedMessage id="comments.level.reply" />
                            </Tag>
                            <Typography.Text style={{ fontSize: 12 }}>
                                <FormattedMessage
                                    id="comments.level.replyTo"
                                    values={{ nickname, uid }}
                                />
                            </Typography.Text>
                        </Space>
                    );
                },
            },
            {
                title: <FormattedMessage id="comments.columns.related" />,
                width: 340,
                render: (_, record) => {
                    const isDramaReview = record.item_type !== 'SHORT_VIDEO';
                    const title = getWorkTitle(record);
                    const cover = getWorkCover(record);
                    const relatedId = isDramaReview
                        ? getDramaId(record)
                        : record.work_id || record.episode?.id || '';
                    const videoUrl = isDramaReview ? '' : getVideoUrl(record);

                    return (
                        <Space align="start" size={12}>
                            {cover ? (
                                <Image
                                    src={resolveAssetUrl(cover)}
                                    width={48}
                                    height={64}
                                    style={{ objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: 48,
                                        height: 64,
                                        borderRadius: 4,
                                        background: 'rgba(0,0,0,0.06)',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                                <Typography.Text>
                                    <FormattedMessage id="comments.related.type" />：
                                    <FormattedMessage
                                        id={
                                            isDramaReview
                                                ? 'comments.related.drama'
                                                : 'comments.related.video'
                                        }
                                    />
                                </Typography.Text>
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    <FormattedMessage
                                        id={
                                            isDramaReview
                                                ? 'comments.related.dramaId'
                                                : 'comments.related.videoId'
                                        }
                                    />
                                    ：
                                    {relatedId ? (
                                        <Typography.Text
                                            copyable={{ text: relatedId }}
                                            style={{ fontSize: 12 }}
                                        >
                                            {relatedId}
                                        </Typography.Text>
                                    ) : (
                                        '-'
                                    )}
                                </Typography.Text>
                                <Typography.Text strong style={{ wordBreak: 'break-word' }}>
                                    {title ? `《${title}》` : '-'}
                                </Typography.Text>
                                {videoUrl ? (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ paddingInline: 0, height: 'auto' }}
                                        onClick={() => setPreviewUrl(resolveAssetUrl(videoUrl))}
                                    >
                                        <FormattedMessage id="comments.preview" />
                                    </Button>
                                ) : null}
                            </Space>
                        </Space>
                    );
                },
            },
            {
                title: <FormattedMessage id="comments.columns.author" />,
                width: 200,
                render: (_, record) => {
                    const nickname = record.user?.nickname?.trim() || '-';
                    const uid = record.user?.id || record.user_id || '';
                    return (
                        <Space direction="vertical" size={2}>
                            <Typography.Text strong>{nickname}</Typography.Text>
                            {uid ? (
                                <Typography.Text copyable={{ text: uid }} type="secondary">
                                    {uid}
                                </Typography.Text>
                            ) : (
                                '-'
                            )}
                        </Space>
                    );
                },
            },
            {
                title: <FormattedMessage id="comments.columns.stats" />,
                width: 160,
                render: (_, record) => (
                    <Space direction="vertical" size={0}>
                        <Typography.Text>
                            <FormattedMessage
                                id="comments.stats.like"
                                values={{ n: toCount(record.like_count) }}
                            />
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            <FormattedMessage
                                id="comments.stats.reply"
                                values={{ n: toCount(record.reply_count) }}
                            />
                        </Typography.Text>
                    </Space>
                ),
            },
            {
                title: <FormattedMessage id="comments.columns.createdAt" />,
                width: 180,
                render: (_, record) => formatDramaTimestamp(record.created_at),
            },
            {
                title: <FormattedMessage id="comments.columns.auditStatus" />,
                width: 110,
                render: (_, record) => {
                    if (isDeleted(record)) {
                        return (
                            <Tag color="default">
                                <FormattedMessage id="comments.auditStatus.DELETED" />
                            </Tag>
                        );
                    }
                    if (record.audit_status === 'PENDING') {
                        return (
                            <Tag color="orange">
                                <FormattedMessage id="comments.auditStatus.PENDING" />
                            </Tag>
                        );
                    }
                    return (
                        <Tag color="green">
                            <FormattedMessage id="comments.auditStatus.APPROVED" />
                        </Tag>
                    );
                },
            },
            {
                title: <FormattedMessage id="comments.columns.action" />,
                width: 140,
                fixed: 'right',
                valueType: 'option',
                render: (_, record) => {
                    const deleted = isDeleted(record);
                    return [
                        <Permission permission={DELETE_PERMISSION} key="delete">
                            <Button
                                type="link"
                                danger
                                size="small"
                                disabled={deleted}
                                onClick={() => {
                                    deleteForm.resetFields();
                                    setDeleteTarget(record);
                                }}
                            >
                                <FormattedMessage id="b.delete" />
                            </Button>
                        </Permission>,
                        <Button
                            key="history"
                            type="link"
                            size="small"
                            onClick={() => {
                                setLogBizId(record.id);
                                setLogOpen(true);
                            }}
                        >
                            <FormattedMessage id="comments.history" />
                        </Button>,
                    ];
                },
            },
        ],
        [deleteForm, formatMessage, pagination.current, pagination.pageSize],
    );

    return (
        <Permission permission={PERMISSION}>
            <ProCard>
                <Form form={searchForm} onFinish={handleSearch} style={{ marginBottom: 16 }}>
                    <Flex gap={16} wrap>
                        <Form.Item name="id" style={{ marginBottom: 0 }}>
                            <Input
                                allowClear
                                placeholder={formatMessage({ id: 'comments.search.id' })}
                                style={{ width: 180 }}
                            />
                        </Form.Item>
                        <Form.Item name="content" style={{ marginBottom: 0 }}>
                            <Input
                                allowClear
                                placeholder={formatMessage({ id: 'comments.search.content' })}
                                style={{ width: 200 }}
                            />
                        </Form.Item>
                        <Form.Item name="user_id" style={{ marginBottom: 0 }}>
                            <Input
                                allowClear
                                placeholder={formatMessage({ id: 'comments.search.userId' })}
                                style={{ width: 180 }}
                            />
                        </Form.Item>
                        <Form.Item name="item_type" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                placeholder={formatMessage({ id: 'comments.search.itemType' })}
                                style={{ width: 140 }}
                                options={[
                                    {
                                        value: 'EPISODE',
                                        label: formatMessage({ id: 'comments.itemType.EPISODE' }),
                                    },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="work_id" style={{ marginBottom: 0 }}>
                            <Input
                                allowClear
                                placeholder={formatMessage({ id: 'comments.search.workId' })}
                                style={{ width: 180 }}
                            />
                        </Form.Item>
                        <Form.Item name="level" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                placeholder={formatMessage({ id: 'comments.search.level' })}
                                style={{ width: 140 }}
                                options={[
                                    {
                                        value: 'root',
                                        label: formatMessage({ id: 'comments.levelFilter.root' }),
                                    },
                                    {
                                        value: 'reply',
                                        label: formatMessage({ id: 'comments.levelFilter.reply' }),
                                    },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="audit_status_ui" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                placeholder={formatMessage({ id: 'comments.search.auditStatus' })}
                                style={{ width: 140 }}
                                options={[
                                    {
                                        value: 'APPROVED',
                                        label: formatMessage({
                                            id: 'comments.auditStatus.APPROVED',
                                        }),
                                    },
                                    {
                                        value: 'DELETED',
                                        label: formatMessage({
                                            id: 'comments.auditStatus.DELETED',
                                        }),
                                    },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="created_at_range" style={{ marginBottom: 0 }}>
                            <DatePicker.RangePicker
                                allowClear
                                placeholder={[
                                    formatMessage({ id: 'comments.search.createdAtStart' }),
                                    formatMessage({ id: 'comments.search.createdAtEnd' }),
                                ]}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            <FormattedMessage id="b.search" />
                        </Button>
                        <Button onClick={handleResetSearch}>
                            <FormattedMessage id="b.reset" />
                        </Button>
                    </Flex>
                </Form>

                <AdminProTable<CommentItem>
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    rowKey={(record) => record.id}
                    search={false}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            void fetchList(page, pageSize);
                        },
                    }}
                    toolBarRender={() => [
                        <Select
                            key="sort"
                            value={sortDirection}
                            style={{ width: 160 }}
                            options={[
                                {
                                    value: 'desc',
                                    label: `${formatMessage({ id: 'comments.sort.label' })} · ${formatMessage({ id: 'comments.sort.desc' })}`,
                                },
                                {
                                    value: 'asc',
                                    label: `${formatMessage({ id: 'comments.sort.label' })} · ${formatMessage({ id: 'comments.sort.asc' })}`,
                                },
                            ]}
                            onChange={(value: SortDirection) => setSortDirection(value)}
                        />,
                    ]}
                    options={{
                        reload: () =>
                            void fetchList(
                                pagination.current as number,
                                pagination.pageSize as number,
                            ),
                        density: true,
                        setting: true,
                    }}
                    scroll={{ x: 1900 }}
                    locale={{
                        emptyText: formatMessage({ id: 'comments.empty' }),
                    }}
                />
            </ProCard>

            <Modal
                open={Boolean(deleteTarget)}
                title={<FormattedMessage id="comments.deleteConfirm" />}
                confirmLoading={deleting}
                okText={formatMessage({ id: 'comments.deleteSubmit' })}
                okButtonProps={{ danger: true }}
                cancelText={formatMessage({ id: 'b.cancel' })}
                destroyOnClose
                onCancel={() => {
                    setDeleteTarget(null);
                    deleteForm.resetFields();
                }}
                onOk={() => deleteForm.submit()}
            >
                <Form form={deleteForm} layout="vertical" onFinish={handleDeleteSubmit}>
                    <Form.Item
                        name="reason"
                        label={<FormattedMessage id="comments.deleteReason" />}
                        rules={[
                            {
                                required: true,
                                message: formatMessage({ id: 'comments.deleteReasonRequired' }),
                            },
                        ]}
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder={formatMessage({
                                id: 'comments.deleteReasonPlaceholder',
                            })}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <CommentVideoPreviewModal
                open={Boolean(previewUrl)}
                src={previewUrl}
                onClose={() => setPreviewUrl(null)}
            />

            <CommentAuditLogDrawer
                open={logOpen}
                commentId={logBizId}
                onClose={() => {
                    setLogOpen(false);
                    setLogBizId(null);
                }}
            />
        </Permission>
    );
};

export default PageComments;
